// Simulation runs entirely in useFrame against delta time, mutating refs.
// No React state is set per frame — UI values are buffered in refs and
// flushed by a parent setInterval at low frequency.

import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { isObstacleAt } from "./MapScene";
import { ZOMBIE_TYPES } from "./levels";
import {
  playBeamStart,
  stopBeam,
  playCollect,
  playCoin,
  playLaser,
  playLightning,
  playWheelHit,
  playBallHit,
  playDogBark,
} from "./sound";

const UFO_HEIGHT = 10;
const BASE_SPEED = 14; // units per second
const BEAM_MAX_HOLD = 10; // seconds the beam can be held continuously

export default function GameSim({
  level,
  obstacles,
  config,
  paused,
  ufoStateRef,
  beamStateRef,
  zombiesRef,
  bossStateRef,
  inputRef,
  uiBufRef,
  beamUIRef,
  setBeamActiveUI,
  setZombiesVersion,
  laserShotsRef,
  companionsRef,
  lightningStrikesRef,
  wheelsRef,
  ballsRef,
  dogsRef,
}) {
  const elapsedRef = useRef(0);
  const mapHalf = level.mapSize / 2 - 2;
  // Reusable math objects so we don't allocate every frame.
  const raycasterRef = useRef(new THREE.Raycaster());
  const ndcRef = useRef(new THREE.Vector2());
  const hitRef = useRef(new THREE.Vector3());
  const planeRef = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), -UFO_HEIGHT));
  const laserCooldownRef = useRef(0);
  const lightningCooldownRef = useRef(2);
  // Throttle gate for reward-power impact SFX so they don't spam.
  const sfxThrottleRef = useRef({ wheel: 0, ball: 0, dog: 0 });

  useFrame((state, delta) => {
    if (paused) return;
    // Clamp dt to keep physics stable on slow frames / tab switches.
    const dt = Math.min(delta, 0.05);
    elapsedRef.current += dt;

    const { speedMult, beamRadius, beamStrength, freezeAmount, multMult } = config;

    // ---------- INPUT & UFO MOVEMENT ----------
    const k = inputRef.current.keys;
    let ix = 0;
    let iz = 0;
    if (k["w"] || k["W"] || k["ArrowUp"]) iz -= 1;
    if (k["s"] || k["S"] || k["ArrowDown"]) iz += 1;
    if (k["a"] || k["A"] || k["ArrowLeft"]) ix -= 1;
    if (k["d"] || k["D"] || k["ArrowRight"]) ix += 1;
    let mag = Math.hypot(ix, iz);

    // Virtual joystick (mobile / touch) takes precedence over the desktop
    // cursor-follow when keyboard isn't pressed.
    const u = ufoStateRef.current;
    const joy = inputRef.current.joystick;
    if (mag === 0 && joy.active && (joy.dx !== 0 || joy.dz !== 0)) {
      ix = joy.dx;
      iz = joy.dz;
      mag = Math.hypot(ix, iz);
    } else if (mag === 0 && !joy.active && inputRef.current.mouse.active && !config.touchMode) {
      // Desktop only: steer toward cursor's projection onto the flight plane.
      ndcRef.current.set(inputRef.current.mouse.x, inputRef.current.mouse.y);
      raycasterRef.current.setFromCamera(ndcRef.current, state.camera);
      const hit = raycasterRef.current.ray.intersectPlane(planeRef.current, hitRef.current);
      if (hit) {
        const dx = hit.x - u.x;
        const dz = hit.z - u.z;
        const dist = Math.hypot(dx, dz);
        if (dist > 0.6) {
          const n = 1 / dist;
          ix = dx * n;
          iz = dz * n;
          const easeRadius = 4;
          if (dist < easeRadius) {
            const ease = dist / easeRadius;
            ix *= ease;
            iz *= ease;
          }
          mag = Math.hypot(ix, iz);
        }
      }
    }

    if (mag > 1) {
      ix /= mag;
      iz /= mag;
    }
    const speed = BASE_SPEED * speedMult;
    const targetVx = ix * speed;
    const targetVz = iz * speed;
    // Frame-rate-independent smoothing.
    const smooth = 1 - Math.pow(0.0008, dt);
    u.vx += (targetVx - u.vx) * smooth;
    u.vz += (targetVz - u.vz) * smooth;
    u.x += u.vx * dt;
    u.z += u.vz * dt;
    if (u.x > mapHalf) u.x = mapHalf;
    if (u.x < -mapHalf) u.x = -mapHalf;
    if (u.z > mapHalf) u.z = mapHalf;
    if (u.z < -mapHalf) u.z = -mapHalf;
    u.tiltX = (-u.vz / speed) * 0.3;
    u.tiltZ = (u.vx / speed) * 0.3;
    u.y = UFO_HEIGHT + Math.sin(elapsedRef.current * 1.5) * 0.3;

    // ---------- BEAM STATE (with 10s hold cooldown) ----------
    // While "locked", the player must release the input before the beam can
    // re-engage. This prevents holding the beam forever.
    const beam = beamStateRef.current;
    const rawWanted = inputRef.current.beam || k[" "] ? true : false;
    if (beam.locked && !rawWanted) {
      // Released — clear the lock so next press can start a fresh beam.
      beam.locked = false;
    }
    const beamWanted = rawWanted && !beam.locked;

    if (beamWanted && !beam.active) {
      beam.active = true;
      beam.holdTime = 0; // fresh 10s window
      if (!beamUIRef.current) {
        beamUIRef.current = true;
        setBeamActiveUI(true);
      }
      playBeamStart();
    } else if (!beamWanted && beam.active) {
      beam.active = false;
      if (beamUIRef.current) {
        beamUIRef.current = false;
        setBeamActiveUI(false);
      }
      stopBeam();
    }

    if (beam.active) {
      beam.holdTime += dt;
      const maxHold = config.beamMaxHold || BEAM_MAX_HOLD;
      if (beam.holdTime >= maxHold) {
        // Auto-release: turn off and lock until input is released.
        beam.active = false;
        beam.locked = true;
        beam.holdTime = maxHold;
        if (beamUIRef.current) {
          beamUIRef.current = false;
          setBeamActiveUI(false);
        }
        stopBeam();
      }
    }
    beam.charge = beam.active ? 1 : Math.min(1, beam.charge + dt * 0.5);

    // ---------- SKY LIGHTNING ----------
    if (config.lightning.enabled) {
      lightningCooldownRef.current -= dt;
      if (lightningCooldownRef.current <= 0) {
        // Pick up to N nearest targets to the UFO within range.
        const candidates = [];
        const range2 = config.lightning.range * config.lightning.range;
        const zArrL = zombiesRef.current;
        for (let i = 0; i < zArrL.length; i++) {
          const z = zArrL[i];
          if (z.removed) continue;
          const dx = z.position[0] - u.x;
          const dz = z.position[2] - u.z;
          const d2 = dx * dx + dz * dz;
          if (d2 < range2) candidates.push({ z, d2 });
        }
        candidates.sort((a, b) => a.d2 - b.d2);
        const hit = candidates.slice(0, config.lightning.targets);
        if (hit.length) {
          if (!lightningStrikesRef.current.nextId) lightningStrikesRef.current.nextId = 0;
          for (const c of hit) {
            const z = c.z;
            const bonus = z.typeData.boss || z.typeData.size > 1.2 ? config.lightning.bigBonus : 1;
            z.pullProgress += (config.lightning.progress * bonus) / z.typeData.hp;
            if (z.pullProgress >= 1) z.beingPulled = true;
            lightningStrikesRef.current.push({
              id: ++lightningStrikesRef.current.nextId,
              x: z.position[0],
              z: z.position[2],
              y: z.position[1] + 1,
              born: state.clock.elapsedTime,
            });
          }
          uiBufRef.current.sfx.push("lightning");
          lightningCooldownRef.current = config.lightning.interval;
        } else {
          lightningCooldownRef.current = 0.5;
        }
      }
      const t = state.clock.elapsedTime;
      const arr = lightningStrikesRef.current;
      let w = 0;
      for (let r = 0; r < arr.length; r++) {
        if (t - arr[r].born < 0.18) arr[w++] = arr[r];
      }
      arr.length = w;
    }

    // ---------- COMPANIONS ----------
    // Smoothly track positions beside the main UFO. Frame-rate-independent.
    // Defensively clamp the iteration to the array length so a misconfigured
    // count can NEVER read past the buffer and crash the render loop.
    const compArr = companionsRef.current;
    if (config.companion.enabled) {
      const k2 = 1 - Math.pow(0.002, dt);
      const compMax = Math.min(config.companion.count, compArr.length);
      for (let i = 0; i < compMax; i++) {
        const c = compArr[i];
        if (!c) continue;
        c.y = u.y;
        c.x += (u.x + c.ox - c.x) * k2;
        c.z += (u.z + c.oz - c.z) * k2;
      }
    }

    // ---------- ROLLING WHEELS ----------
    if (config.wheel.enabled) {
      const zArr = zombiesRef.current;
      const wArr = wheelsRef.current;
      const sfx = sfxThrottleRef.current;
      // Lazy spawn to match the current count.
      while (wArr.length < config.wheel.count) {
        wArr.push({
          angle: (Math.PI * 2 * wArr.length) / Math.max(1, config.wheel.count),
          y: 0.6,
        });
      }
      if (wArr.length > config.wheel.count) wArr.length = config.wheel.count;
      const orbitR = config.wheel.orbitRadius;
      const omega = config.wheel.angularSpeed;
      const wSize = config.wheel.size;
      const wProg = config.wheel.progress;
      for (let i = 0; i < wArr.length; i++) {
        const w = wArr[i];
        w.angle += omega * dt;
        w.x = u.x + Math.cos(w.angle) * orbitR;
        w.z = u.z + Math.sin(w.angle) * orbitR;
        // Continuous-contact damage.
        for (let j = 0; j < zArr.length; j++) {
          const z = zArr[j];
          if (z.removed || z.beingPulled) continue;
          const dx = z.position[0] - w.x;
          const dz = z.position[2] - w.z;
          const r = wSize + 0.7 * z.typeData.size;
          if (dx * dx + dz * dz < r * r) {
            z.pullProgress += (wProg * dt) / z.typeData.hp;
            if (z.pullProgress >= 1) z.beingPulled = true;
            const tm = performance.now();
            if (tm - sfx.wheel > 220) {
              playWheelHit();
              sfx.wheel = tm;
            }
          }
        }
      }
    }

    // ---------- BOUNCING BALLS ----------
    if (config.ball.enabled) {
      const zArr = zombiesRef.current;
      const bArr = ballsRef.current;
      const sfx = sfxThrottleRef.current;
      while (bArr.length < config.ball.count) {
        const ang = Math.random() * Math.PI * 2;
        bArr.push({
          x: u.x,
          z: u.z,
          vx: Math.cos(ang) * config.ball.speed,
          vz: Math.sin(ang) * config.ball.speed,
          hitCd: 0,
          y: 1.0,
        });
      }
      if (bArr.length > config.ball.count) bArr.length = config.ball.count;
      const bSize = config.ball.size;
      const bProg = config.ball.progress;
      const limit = mapHalf - 1;
      for (let i = 0; i < bArr.length; i++) {
        const b = bArr[i];
        // Normalize speed so picks accelerate but never let balls slow down.
        const sp = Math.hypot(b.vx, b.vz);
        if (sp > 0) {
          const target = config.ball.speed;
          const n = target / sp;
          b.vx *= n;
          b.vz *= n;
        }
        b.x += b.vx * dt;
        b.z += b.vz * dt;
        // Bounce off map borders.
        if (b.x > limit) { b.x = limit; b.vx = -Math.abs(b.vx); }
        if (b.x < -limit) { b.x = -limit; b.vx = Math.abs(b.vx); }
        if (b.z > limit) { b.z = limit; b.vz = -Math.abs(b.vz); }
        if (b.z < -limit) { b.z = -limit; b.vz = Math.abs(b.vz); }
        if (b.hitCd > 0) b.hitCd -= dt;
        // Zombie collision (gated per-ball cooldown so it doesn't spam).
        if (b.hitCd <= 0) {
          for (let j = 0; j < zArr.length; j++) {
            const z = zArr[j];
            if (z.removed || z.beingPulled) continue;
            const dx = z.position[0] - b.x;
            const dz = z.position[2] - b.z;
            const r = bSize + 0.8 * z.typeData.size;
            if (dx * dx + dz * dz < r * r) {
              z.pullProgress += bProg / z.typeData.hp;
              if (z.pullProgress >= 1) z.beingPulled = true;
              // Reflect velocity off the impact normal.
              const d = Math.hypot(dx, dz) || 1;
              const nx = dx / d;
              const nz = dz / d;
              const dot = b.vx * nx + b.vz * nz;
              b.vx -= 2 * dot * nx;
              b.vz -= 2 * dot * nz;
              b.hitCd = config.ball.contactCd;
              const tm = performance.now();
              if (tm - sfx.ball > 90) {
                playBallHit();
                sfx.ball = tm;
              }
              break;
            }
          }
        }
      }
    }

    // ---------- SUPER DOGS ----------
    if (config.dog.enabled) {
      const zArr = zombiesRef.current;
      const dArr = dogsRef.current;
      const sfx = sfxThrottleRef.current;
      while (dArr.length < config.dog.count) {
        dArr.push({
          x: u.x + (Math.random() - 0.5) * 4,
          z: u.z + (Math.random() - 0.5) * 4,
          vx: 0,
          vz: 0,
          targetId: -1,
          hitCd: 0,
        });
      }
      if (dArr.length > config.dog.count) dArr.length = config.dog.count;
      const dSpeed = config.dog.speed;
      const dSize = config.dog.size;
      const dProg = config.dog.progress;
      for (let i = 0; i < dArr.length; i++) {
        const d = dArr[i];
        if (d.hitCd > 0) d.hitCd -= dt;
        // (Re)acquire target.
        let target = null;
        if (d.targetId >= 0) {
          for (let j = 0; j < zArr.length; j++) {
            if (zArr[j].id === d.targetId && !zArr[j].removed && !zArr[j].beingPulled) {
              target = zArr[j];
              break;
            }
          }
        }
        if (!target) {
          let best = null;
          let bestD2 = Infinity;
          for (let j = 0; j < zArr.length; j++) {
            const z = zArr[j];
            if (z.removed || z.beingPulled) continue;
            const dx = z.position[0] - d.x;
            const dz = z.position[2] - d.z;
            const d2 = dx * dx + dz * dz;
            if (d2 < bestD2) { bestD2 = d2; best = z; }
          }
          if (best) {
            target = best;
            d.targetId = best.id;
          }
        }
        if (target) {
          const dx = target.position[0] - d.x;
          const dz = target.position[2] - d.z;
          const dist = Math.hypot(dx, dz);
          if (dist > 0.001) {
            d.vx = (dx / dist) * dSpeed;
            d.vz = (dz / dist) * dSpeed;
          }
          d.x += d.vx * dt;
          d.z += d.vz * dt;
          const r = dSize + 0.8 * target.typeData.size;
          if (d.hitCd <= 0 && dist < r) {
            target.pullProgress += dProg / target.typeData.hp;
            if (target.pullProgress >= 1) target.beingPulled = true;
            d.hitCd = config.dog.contactCd;
            d.targetId = -1;
            const tm = performance.now();
            if (tm - sfx.dog > 180) {
              playDogBark();
              sfx.dog = tm;
            }
          }
        } else {
          // No target — gently drift toward UFO so dog stays on-screen.
          const dx = u.x - d.x;
          const dz = u.z - d.z;
          const dist = Math.hypot(dx, dz);
          if (dist > 4) {
            d.x += (dx / dist) * dSpeed * 0.5 * dt;
            d.z += (dz / dist) * dSpeed * 0.5 * dt;
          }
        }
      }
    }

    // ---------- ZOMBIES ----------
    const zArr = zombiesRef.current;
    const freezeMult = beam.active ? Math.max(0.1, 1 - freezeAmount * 3) : 1;
    const waveSpeed = config.waveSpeedBoost || 1;
    const magnet = config.magnet;
    let removedAny = false;

    // ---------- AUTO LASER ----------
    if (config.laser.enabled) {
      laserCooldownRef.current -= dt;
      if (laserCooldownRef.current <= 0) {
        let best = null;
        let bestD2 = config.laser.range * config.laser.range;
        for (let i = 0; i < zArr.length; i++) {
          const z = zArr[i];
          if (z.removed || z.beingPulled) continue;
          const dx = z.position[0] - u.x;
          const dz = z.position[2] - u.z;
          const d2 = dx * dx + dz * dz;
          if (d2 < bestD2) {
            bestD2 = d2;
            best = z;
          }
        }
        if (best) {
          best.pullProgress += config.laser.progressPerHit / best.typeData.hp;
          if (best.pullProgress >= 1) best.beingPulled = true;
          if (!laserShotsRef.current.nextId) laserShotsRef.current.nextId = 0;
          laserShotsRef.current.push({
            id: ++laserShotsRef.current.nextId,
            from: [u.x, u.y - 0.4, u.z],
            to: [best.position[0], best.position[1] + 0.8, best.position[2]],
            born: state.clock.elapsedTime,
          });
          uiBufRef.current.sfx.push("laser");
          laserCooldownRef.current = 1 / Math.max(0.25, config.laser.rate);
        } else {
          laserCooldownRef.current = 0.15;
        }
      }
      const t = state.clock.elapsedTime;
      const arr = laserShotsRef.current;
      let w = 0;
      for (let r = 0; r < arr.length; r++) {
        if (t - arr[r].born < 0.15) arr[w++] = arr[r];
      }
      arr.length = w;
    }
    for (let i = 0; i < zArr.length; i++) {
      const z = zArr[i];
      if (z.removed) continue;
      const td = z.typeData;
      if (!z.beingPulled) {
        z.wanderTimer -= dt;
        if (z.wanderTimer <= 0) {
          const dx = u.x - z.position[0];
          const dz = u.z - z.position[2];
          const angleToUFO = Math.atan2(dx, dz);
          const wanderJitter = (Math.random() - 0.5) * 2.5;
          z.wanderDir = angleToUFO + wanderJitter;
          z.wanderTimer = 1 + Math.random() * 2;
        }
        const moveSpeed = td.speed * freezeMult * waveSpeed;
        let nx = z.position[0] + Math.sin(z.wanderDir) * moveSpeed * dt;
        let nz = z.position[2] + Math.cos(z.wanderDir) * moveSpeed * dt;
        // Magnet: gentle pull toward UFO if within range.
        if (magnet.enabled) {
          const dxm = u.x - z.position[0];
          const dzm = u.z - z.position[2];
          const dm = Math.hypot(dxm, dzm);
          if (dm > 0.5 && dm < magnet.range) {
            const pull = magnet.strength * dt;
            nx += (dxm / dm) * pull;
            nz += (dzm / dm) * pull;
          }
        }
        if (
          Math.abs(nx) < mapHalf &&
          Math.abs(nz) < mapHalf &&
          !isObstacleAt(obstacles, nx, nz, 0.6 * td.size)
        ) {
          z.position[0] = nx;
          z.position[2] = nz;
        } else {
          z.wanderDir += Math.PI * 0.7 + Math.random();
          z.wanderTimer = 0.5;
        }
        z.rotation = z.wanderDir;

        if (beam.active) {
          const dx = z.position[0] - u.x;
          const dz = z.position[2] - u.z;
          const dist = Math.hypot(dx, dz);
          const effRadius = beamRadius * (1 + ((UFO_HEIGHT - z.position[1]) / UFO_HEIGHT) * 0.2);
          if (dist < effRadius) {
            z.beingPulled = true;
            z.pullSrcX = u.x;
            z.pullSrcZ = u.z;
            z.pullSrcStr = 1.0;
          } else if (config.companion.enabled) {
            const compR = beamRadius * config.companion.radiusMul;
            const compMax2 = Math.min(config.companion.count, compArr.length);
            for (let ci = 0; ci < compMax2; ci++) {
              const c = compArr[ci];
              if (!c) continue;
              const cdx = z.position[0] - c.x;
              const cdz = z.position[2] - c.z;
              if (Math.hypot(cdx, cdz) < compR) {
                z.beingPulled = true;
                z.pullSrcX = c.x;
                z.pullSrcZ = c.z;
                z.pullSrcStr = config.companion.strengthMul;
                break;
              }
            }
          }
        }
      } else {
        const srcX = z.pullSrcX != null ? z.pullSrcX : u.x;
        const srcZ = z.pullSrcZ != null ? z.pullSrcZ : u.z;
        const srcStr = z.pullSrcStr != null ? z.pullSrcStr : 1.0;
        const dx = z.position[0] - srcX;
        const dz = z.position[2] - srcZ;
        const dist = Math.hypot(dx, dz);
        const effR = beamRadius * (srcStr < 1 ? config.companion.radiusMul : 1);
        if (!beam.active || dist > effR + 1) {
          z.beingPulled = false;
          z.pullProgress = Math.max(0, z.pullProgress - dt * 1.5);
          z.position[1] = Math.max(0, z.position[1] - 8 * dt);
        } else {
          z.pullProgress += ((beamStrength * srcStr) / td.hp) * dt;
          const xzPull = 1 - Math.pow(0.02, dt);
          z.position[0] += (srcX - z.position[0]) * xzPull;
          z.position[2] += (srcZ - z.position[2]) * xzPull;
          z.position[1] = z.pullProgress * (UFO_HEIGHT - 1.5);
          z.rotation += dt * 6;
          if (z.pullProgress >= 1) {
            // Buffer the collection — flush later by parent.
            z.removed = true;
            removedAny = true;
            const coins = Math.round(td.coins * multMult);
            const buf = uiBufRef.current;
            buf.coins += coins;
            buf.score += td.score;
            buf.collected += 1;
            buf.popups.push({
              id: buf.popupNextId++,
              text: `+${td.score}`,
              sub: `+${coins} coin${coins === 1 ? "" : "s"}`,
              color: td.color,
            });
            buf.sfx.push("collect");
            if (td.coins >= 5) buf.sfx.push("coin");
            bossStateRef.current.collectedCount += 1;
          }
        }
      }
    }

    // ---------- BOSS SPAWN (Level 5) ----------
    if (
      level.boss &&
      !bossStateRef.current.spawned &&
      bossStateRef.current.collectedCount >= Math.floor(level.goal * 0.6)
    ) {
      bossStateRef.current.spawned = true;
      const angle = Math.random() * Math.PI * 2;
      const dist = mapHalf * 0.6;
      const bossHpMult = config.bossHpTalentMult || 1;
      zArr.push({
        id: 99999,
        type: "boss",
        typeData: { ...ZOMBIE_TYPES.boss, hp: ZOMBIE_TYPES.boss.hp * bossHpMult },
        position: [Math.cos(angle) * dist, 0, Math.sin(angle) * dist],
        rotation: 0,
        wanderTimer: 1,
        wanderDir: Math.random() * Math.PI * 2,
        beingPulled: false,
        pullProgress: 0,
        removed: false,
        isBoss: true,
      });
      removedAny = true; // force re-render to mount boss
    }

    if (removedAny) {
      // Increment version asynchronously so React can re-render the zombie
      // list, but this happens at most a few times per second under play.
      setZombiesVersion((v) => v + 1);
    }

    // Accumulate elapsed time for timer flush (used by parent flusher).
    uiBufRef.current.elapsedDt += dt;
  });

  return null;
}
